import { getAssetsPath } from "./utils/pathConfig";
import { FEATURES_CONFIG } from "./featuresConfig";
import { SFX_SPRITE_MAP } from "./sfxSpriteMap"; // AUTO-GENERATED — run "npm run build:sfx" to regenerate

// Use centralized path configuration
const BASE_PATH = getAssetsPath();

// ============================================================================
// AUDIO CONFIGURATION
// ============================================================================
export const AUDIO_CONFIG = {
  AMBIENT_MUSIC_ENABLED: false, // Enable/disable ambient music layer (plays alongside background music)
};

// ============================================================================
// AUDIO SPRITE CONFIGURATION
// ============================================================================
// SFX_SPRITE_MAP imported above from auto-generated sfxSpriteMap.js

// ============================================================================
// CONDITIONAL SOUND LOADING - Build sounds array based on enabled features
// ============================================================================
const buildSoundsArray = () => {
  const sounds = [];

  // ========================================================================
  // CORE LOOPING SOUNDS - Always loaded
  // ========================================================================
  sounds.push(
    {
      name: "background_music",
      url: `${BASE_PATH}/sounds/background_music.mp3`,
      loop: true,
      volume: 1,
    },
    {
      name: "reel_spin",
      url: `${BASE_PATH}/sounds/reel_spin.mp3`,
      volume: 1,
      loop: true,
    },
    {
      name: "counter",
      url: `${BASE_PATH}/sounds/counter.mp3`,
      loop: true,
      volume: 1,
    },
    {
      name: "win_music",
      url: `${BASE_PATH}/sounds/win_music.mp3`,
      loop: true,
      volume: 1,
    },
  );

  // ========================================================================
  // MAX WIN MUSIC - Only load if max win is enabled
  // ========================================================================
  if (FEATURES_CONFIG.MAX_WIN.ENABLED) {
    sounds.push({
      name: "max_win_music",
      url: `${BASE_PATH}/sounds/max_win_music.mp3`,
      loop: true,
      volume: 1,
    });
  }

  // ========================================================================
  // WIN LINE SOUND - Only load if win lines are enabled
  // ========================================================================
  if (FEATURES_CONFIG.WIN_LINES.ENABLED) {
    sounds.push({
      name: "win_line",
      url: `${BASE_PATH}/sounds/win_line.mp3`,
      loop: false,
      volume: 1,
    });
  }

  // ========================================================================
  // AMBIENT MUSIC - Optional audio layer (plays alongside background music)
  // ========================================================================
  if (AUDIO_CONFIG.AMBIENT_MUSIC_ENABLED) {
    sounds.push({
      name: "ambient_music",
      url: `${BASE_PATH}/sounds/ambient_music.mp3`,
      loop: true,
      volume: 1,
    });
  }

  // ========================================================================
  // TENSION FEATURE SOUNDS - Only load if tension is enabled
  // ========================================================================
  if (FEATURES_CONFIG.TENSION.ENABLED) {
    sounds.push({
      name: "tension_shake",
      url: `${BASE_PATH}/sounds/tension_shake.mp3`,
      loop: true,
      volume: 1,
    });
  }

  // ========================================================================
  // HOLD AND WIN FEATURE SOUNDS - Only load if Hold and Win is enabled
  // ========================================================================
  if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
    sounds.push(
      {
        name: "feature_background_music",
        url: `${BASE_PATH}/sounds/feature_background_music.mp3`,
        loop: true,
        volume: 1,
      },
      {
        name: "feature_reel_spin",
        url: `${BASE_PATH}/sounds/feature_reel_spin.mp3`,
        volume: 1,
        loop: true,
      },
      {
        name: "collector_counter",
        url: `${BASE_PATH}/sounds/collector_counter.mp3`,
        loop: true,
        volume: 1,
      },
      {
        name: "progress_bar_tension",
        url: `${BASE_PATH}/sounds/progress_bar_tension.mp3`,
        loop: true,
        volume: 1,
      },
      {
        name: "hold_and_win_music",
        url: `${BASE_PATH}/sounds/hold_and_win_music.mp3`,
        loop: false,
        volume: 1,
      },
      {
        name: "total_win_loop",
        url: `${BASE_PATH}/sounds/total_win_loop.mp3`,
        loop: true,
        volume: 1,
      },
    );
  }

  // ========================================================================
  // SFX AUDIO SPRITE - All one-shot sounds combined (48 sounds → 1 file)
  // Note: Sprite file contains all sounds, but only enabled feature sounds
  // will be accessible via SOUND_NAME export
  // ========================================================================
  sounds.push({
    name: "sfx_sprite",
    url: `${BASE_PATH}/sounds/sfx-sprite.mp3`,
    sprite: SFX_SPRITE_MAP,
    volume: 1,
  });

  return sounds;
};

export const GAME_SOUNDS = {
  sounds: buildSoundsArray(),
};

// ============================================================================
// SOUND NAME MAPPINGS
// All sound names registered here. If a sound is disabled in the sprite
// manifest or not loaded as an individual Howl, soundManager.play() silently
// does nothing — no conditional logic needed.
// ============================================================================
export const SOUND_NAME = {
  // Looping / individual sounds
  background_music: "background_music",
  ambient_music: "ambient_music",
  reel_spin: "reel_spin",
  counter: "counter",
  win_music: "win_music",
  max_win_music: "max_win_music",
  win_line: "win_line",
  tension_shake: "tension_shake",
  feature_background_music: "feature_background_music",
  feature_reel_spin: "feature_reel_spin",
  collector_counter: "collector_counter",
  progress_bar_tension: "progress_bar_tension",
  hold_and_win_music: "hold_and_win_music",
  total_win_loop: "total_win_loop",

  // One-shot sounds (sprite)
  win: "win",
  big_win: "big_win",
  mega_win: "mega_win",
  super_win: "super_win",
  epic_win: "epic_win",
  super_mega_win: "super_mega_win",
  button_click: "button_click",
  spin: "spin",
  reel_stop: "reel_stop",
  scatter_1: "scatter_1",
  scatter_2: "scatter_2",
  wild: "wild",
  tension: "tension",
  feature_tension: "feature_tension",
  max_win_in: "max_win_in",
  max_win_out: "max_win_out",
  win_music_end: "win_music_end",
  transition_in: "transition_in",
  transition_outro: "transition_outro",
  free_spin_transition: "free_spin_transition",
  free_spin_intro: "free_spin_intro",
  free_spin_outro: "free_spin_outro",
  free_spin_retrigger: "free_spin_retrigger",
  expanding_wild: "expanding_wild",
  expanding_wild_multiplier: "expanding_wild_multiplier",
  buy_bonus: "buy_bonus",

  // Hold and Win one-shot sounds (sprite)
  add_respin: "add_respin",
  coin: "coin",
  coin_multiplier: "coin_multiplier",
  coin_win: "coin_win",
  collector_add: "collector_add",
  collector_appear: "collector_appear",
  collector_particle: "collector_particle",
  progress_bar_add: "progress_bar_add",
  progress_bar_milestone: "progress_bar_milestone",
  progress_bar_multiplier: "progress_bar_multiplier",
  hold_and_win_in: "hold_and_win_in",
  hold_and_win_out: "hold_and_win_out",
  hold_and_win_reload_in: "hold_and_win_reload_in",
  hold_and_win_reload_out: "hold_and_win_reload_out",
  feature_end: "feature_end",
  feature_reel_stop: "feature_reel_stop",
  total_win_in: "total_win_in",
  total_win_out: "total_win_out",
  expanding_wild_x2: "coin_multiplier",

  // Drop sounds
  drop_1: "drop_1",
  drop_2: "drop_2",
  drop_3: "drop_3",
  drop_4: "drop_4",
  drop_5: "drop_5",
  drop_6: "drop_6",
  drop_7: "drop_7",
  drop_8: "drop_8",
  drop_9: "drop_9",
  drop_10: "drop_10",
  drop_11: "drop_11",
  drop_12: "drop_12",
};
